import React, { useState, useMemo } from 'react';
import { Radio, Select, Input, InputNumber, Row, Col, Card, Divider } from 'antd';
import { useFormContext } from 'react-hook-form';
import { formatBRL } from '../../../utils/currency';

const AVISTA = 'avista';
const PARCELADA_LOCAL = 'parcelado_local';
const PARCELADA_REMOTO = 'parcelado_remoto';

const tiposAvista = ['Boleto', 'Cartão de crédito', 'Financiamento', 'Boleto e Financiamento', 'Cartão de crédito e Financiamento', 'Boleto e Cartão de crédito'];
const tiposParceladaLocal = ['Boleto', 'Boleto e Financiamento', 'Boleto e Cartão de crédito'];

const formasBoletoLocal = [
  'Entrada à vista e uma parcela em 30 dias',
  'Entrada à vista, uma parcela em 30 dias e a segunda parcela em 60 dias',
  'Entrada à vista, uma parcela em 30 dias, a segunda parcela em 60 dias e a terceira parcela em 90 dias',
  'Entrada à vista e uma parcela em 60 dias',
  'Entrada à vista e a segunda parcela após a instalação da usina',
];

const formasBoletoRemoto = [
  'Um boleto para pagamento em 3 dias úteis',
  'Um boleto para pagamento em 3 dias úteis e uma parcela em 30 dias',
  'Um boleto para pagamento em 3 dias úteis, uma parcela em 30 dias e a segunda parcela em 60 dias',
  'Um boleto para pagamento em 3 dias úteis, uma parcela em 30 dias, a segunda parcela em 60 dias e a terceira parcela em 90 dias',
  'Um boleto para pagamento em 3 dias úteis e uma parcela em 60 dias',
  'Um boleto para pagamento em 3 dias úteis e a segunda parcela após a instalação da usina',
];

const tiposPosParecerRemoto = ['Boleto', 'Cartão de crédito após a aprovação do parecer de acesso', 'Financiamento após a aprovação do parecer de acesso', 'Boleto e Financiamento', 'Cartão de crédito e Financiamento', 'Boleto e Cartão de crédito'];
const entradasRemoto = ['10% de entrada', '5% de entrada'];
const meiosEntradaRemoto10 = ['Boleto'];
const meiosEntradaRemoto5 = ['Boleto', 'Cartão de crédito'];

const bancos = ['Banco do Brasil', 'Bradesco', 'BV', 'Santander', 'Sicoob', 'Sicredi Agência', 'Sicredi Plataforma', 'Plataforma Santander', 'SolFácil'];

const tabelaJuros: Record<number, number> = {
  1: 0.0342, 2: 0.0458, 3: 0.054, 4: 0.0624, 5: 0.0708, 6: 0.0792,
  7: 0.09, 8: 0.0985, 9: 0.107, 10: 0.1158, 11: 0.1244, 12: 0.1331,
  13: 0.1419, 14: 0.1508, 15: 0.1597, 16: 0.1685, 17: 0.1775, 18: 0.1865,
  19: 0.1956, 20: 0.2047, 21: 0.2138,
};

function calcJurosCartao(valor: number, parcelas: number) {
  const fator = tabelaJuros[parcelas] || 0;
  const juros = valor * fator;
  return { juros, total: valor + juros };
}

export default function ParcelCalculator() {
  const { setValue, watch } = useFormContext();
  const valorProposta = watch('valor_proposta') || 0;

  const [formaGeral, setFormaGeral] = useState(AVISTA);
  const [tipoSel, setTipoSel] = useState('');
  const [formaBoleto, setFormaBoleto] = useState('');
  const [banco, setBanco] = useState('');
  const [entradaRemotoPerc, setEntradaRemotoPerc] = useState('');
  const [meioEntrada, setMeioEntrada] = useState('');
  const [tipoPosParecerSel, setTipoPosParecerSel] = useState('');
  const [formaBoletoRemotoSel, setFormaBoletoRemotoSel] = useState('');

  // Values for boleto splits
  const [entrada, setEntrada] = useState(0);
  const [p1, setP1] = useState(0);
  const [p2, setP2] = useState(0);
  const [p3, setP3] = useState(0);

  // Card values
  const [cartaoValor, setCartaoValor] = useState(0);
  const [cartaoParcelas, setCartaoParcelas] = useState(0);

  // Financing
  const [finValor, setFinValor] = useState(0);

  const cartaoCalc = useMemo(() => calcJurosCartao(cartaoValor, cartaoParcelas), [cartaoValor, cartaoParcelas]);

  const totalBoleto = entrada + p1 + p2 + p3;

  const buildDescricao = (): string => {
    const parts: string[] = [];
    if (formaGeral === AVISTA) {
      parts.push(`100% à vista - ${tipoSel}`);
      if (tipoSel.includes('Cartão')) parts.push(`Cartão: ${formatBRL(cartaoCalc.total)} (${cartaoParcelas}x com juros ${formatBRL(cartaoCalc.juros)})`);
      if (tipoSel.includes('Financiamento')) parts.push(`Financiamento: ${formatBRL(finValor)} - Banco: ${banco}`);
      if (tipoSel === 'Boleto') parts.push(`Boleto: ${formatBRL(valorProposta)}`);
    } else if (formaGeral === PARCELADA_LOCAL) {
      parts.push(`Parcelada Local - ${tipoSel}`);
      parts.push(`Forma boleto: ${formaBoleto}`);
      parts.push(`Total boleto: ${formatBRL(totalBoleto)}`);
    } else {
      parts.push(`Parcelada Remoto - ${entradaRemotoPerc}`);
      parts.push(`Meio entrada: ${meioEntrada}`);
      parts.push(`Pós parecer: ${tipoPosParecerSel}`);
    }
    return parts.join(' | ');
  };

  const handleSave = () => {
    setValue('modo_pagamento', formaGeral);
    setValue('descricao_pagamento', buildDescricao());
    setValue('payload_financeiro', {
      formaGeral,
      tipoSelecionado: tipoSel,
      formaBoleto,
      banco,
      entradaPercentual: entradaRemotoPerc,
      meioEntrada,
      tipoPosParecerSel,
      valores: { entrada, p1, p2, p3, cartaoValor, cartaoParcelas, cartaoTotal: cartaoCalc.total, finValor },
      descricao: buildDescricao(),
    });
  };

  const meiosEntrada = entradaRemotoPerc === '10% de entrada' ? meiosEntradaRemoto10 : meiosEntradaRemoto5;

  return (
    <div className="border rounded-lg p-4 mb-4">
      <h3 className="text-lg font-semibold mb-4">Forma de Pagamento</h3>

      <Radio.Group
        value={formaGeral}
        onChange={(e) => { setFormaGeral(e.target.value); setTipoSel(''); setFormaBoleto(''); }}
        className="mb-4"
      >
        <Radio.Button value={AVISTA}>100% à Vista</Radio.Button>
        <Radio.Button value={PARCELADA_LOCAL}>Parcelada Local</Radio.Button>
        <Radio.Button value={PARCELADA_REMOTO}>Parcelada Remoto</Radio.Button>
      </Radio.Group>

      {/* A VISTA */}
      {formaGeral === AVISTA && (
        <div>
          <Select
            className="w-full mb-3"
            placeholder="Tipo de pagamento"
            value={tipoSel || undefined}
            onChange={setTipoSel}
            options={tiposAvista.map((t) => ({ label: t, value: t }))}
          />
          {tipoSel === 'Boleto' && (
            <p className="text-sm text-gray-600">Valor: {formatBRL(valorProposta)}</p>
          )}
          {tipoSel?.includes('Cartão') && (
            <Row gutter={16}>
              <Col span={8}>
                <label className="text-xs">Valor no Cartão</label>
                <InputNumber className="w-full" value={cartaoValor} onChange={(v) => setCartaoValor(v || 0)} prefix="R$" />
              </Col>
              <Col span={8}>
                <label className="text-xs">Parcelas (1-21)</label>
                <InputNumber className="w-full" min={1} max={21} value={cartaoParcelas} onChange={(v) => setCartaoParcelas(v || 0)} />
              </Col>
              <Col span={8}>
                <p className="text-xs mt-4">Total com juros: {formatBRL(cartaoCalc.total)}</p>
                <p className="text-xs text-gray-500">Juros: {formatBRL(cartaoCalc.juros)}</p>
              </Col>
            </Row>
          )}
          {tipoSel?.includes('Financiamento') && (
            <Row gutter={16} className="mt-2">
              <Col span={12}>
                <label className="text-xs">Valor Financiamento</label>
                <InputNumber className="w-full" value={finValor} onChange={(v) => setFinValor(v || 0)} prefix="R$" />
              </Col>
              <Col span={12}>
                <label className="text-xs">Banco</label>
                <Select className="w-full" value={banco || undefined} onChange={setBanco} options={bancos.map((b) => ({ label: b, value: b }))} />
              </Col>
            </Row>
          )}
        </div>
      )}

      {/* PARCELADA LOCAL */}
      {formaGeral === PARCELADA_LOCAL && (
        <div>
          <Select
            className="w-full mb-3"
            placeholder="Tipo de pagamento"
            value={tipoSel || undefined}
            onChange={setTipoSel}
            options={tiposParceladaLocal.map((t) => ({ label: t, value: t }))}
          />
          <Select
            className="w-full mb-3"
            placeholder="Forma de boleto"
            value={formaBoleto || undefined}
            onChange={setFormaBoleto}
            options={formasBoletoLocal.map((f) => ({ label: f, value: f }))}
          />
          {formaBoleto && (
            <Card size="small" className="mb-3">
              <Row gutter={16}>
                <Col span={6}><label className="text-xs">Entrada</label><InputNumber className="w-full" value={entrada} onChange={(v) => setEntrada(v || 0)} prefix="R$" /></Col>
                <Col span={6}><label className="text-xs">P1</label><InputNumber className="w-full" value={p1} onChange={(v) => setP1(v || 0)} prefix="R$" /></Col>
                {formaBoleto.includes('segunda parcela') && <Col span={6}><label className="text-xs">P2</label><InputNumber className="w-full" value={p2} onChange={(v) => setP2(v || 0)} prefix="R$" /></Col>}
                {formaBoleto.includes('terceira parcela') && <Col span={6}><label className="text-xs">P3</label><InputNumber className="w-full" value={p3} onChange={(v) => setP3(v || 0)} prefix="R$" /></Col>}
              </Row>
              <p className="text-sm font-medium mt-2">Total Boleto: {formatBRL(totalBoleto)}</p>
            </Card>
          )}
          {tipoSel?.includes('Financiamento') && (
            <Row gutter={16}>
              <Col span={12}><label className="text-xs">Valor Financiamento</label><InputNumber className="w-full" value={finValor} onChange={(v) => setFinValor(v || 0)} prefix="R$" /></Col>
              <Col span={12}><label className="text-xs">Banco</label><Select className="w-full" value={banco || undefined} onChange={setBanco} options={bancos.map((b) => ({ label: b, value: b }))} /></Col>
            </Row>
          )}
          {tipoSel?.includes('Cartão') && (
            <Row gutter={16} className="mt-2">
              <Col span={8}><label className="text-xs">Valor Cartão</label><InputNumber className="w-full" value={cartaoValor} onChange={(v) => setCartaoValor(v || 0)} prefix="R$" /></Col>
              <Col span={8}><label className="text-xs">Parcelas</label><InputNumber className="w-full" min={1} max={21} value={cartaoParcelas} onChange={(v) => setCartaoParcelas(v || 0)} /></Col>
              <Col span={8}><p className="text-xs mt-4">Total: {formatBRL(cartaoCalc.total)}</p></Col>
            </Row>
          )}
        </div>
      )}

      {/* PARCELADA REMOTO */}
      {formaGeral === PARCELADA_REMOTO && (
        <div>
          <Row gutter={16} className="mb-3">
            <Col span={12}>
              <Select className="w-full" placeholder="Entrada" value={entradaRemotoPerc || undefined} onChange={(v) => { setEntradaRemotoPerc(v); setMeioEntrada(''); }} options={entradasRemoto.map((e) => ({ label: e, value: e }))} />
            </Col>
            <Col span={12}>
              <Select className="w-full" placeholder="Meio de entrada" value={meioEntrada || undefined} onChange={setMeioEntrada} options={meiosEntrada.map((m) => ({ label: m, value: m }))} disabled={!entradaRemotoPerc} />
            </Col>
          </Row>

          <Divider className="my-2">Após Parecer de Acesso</Divider>

          <Select className="w-full mb-3" placeholder="Tipo pós parecer" value={tipoPosParecerSel || undefined} onChange={setTipoPosParecerSel} options={tiposPosParecerRemoto.map((t) => ({ label: t, value: t }))} />

          {tipoPosParecerSel?.includes('Boleto') && (
            <Select className="w-full mb-3" placeholder="Forma boleto remoto" value={formaBoletoRemotoSel || undefined} onChange={setFormaBoletoRemotoSel} options={formasBoletoRemoto.map((f) => ({ label: f, value: f }))} />
          )}

          {tipoPosParecerSel?.includes('Financiamento') && (
            <Row gutter={16}>
              <Col span={12}><label className="text-xs">Valor Financiamento</label><InputNumber className="w-full" value={finValor} onChange={(v) => setFinValor(v || 0)} prefix="R$" /></Col>
              <Col span={12}><label className="text-xs">Banco</label><Select className="w-full" value={banco || undefined} onChange={setBanco} options={bancos.map((b) => ({ label: b, value: b }))} /></Col>
            </Row>
          )}

          {tipoPosParecerSel?.includes('Cartão') && (
            <Row gutter={16} className="mt-2">
              <Col span={8}><label className="text-xs">Valor Cartão</label><InputNumber className="w-full" value={cartaoValor} onChange={(v) => setCartaoValor(v || 0)} prefix="R$" /></Col>
              <Col span={8}><label className="text-xs">Parcelas</label><InputNumber className="w-full" min={1} max={21} value={cartaoParcelas} onChange={(v) => setCartaoParcelas(v || 0)} /></Col>
              <Col span={8}><p className="text-xs mt-4">Total: {formatBRL(cartaoCalc.total)}</p></Col>
            </Row>
          )}
        </div>
      )}

      <Divider />
      <button
        type="button"
        onClick={handleSave}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
      >
        Confirmar Pagamento
      </button>
    </div>
  );
}
